import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as ImportStudentsIcon } from "../../../assets/importStudents.svg";
import { useSnack } from "../../../hooks/useSnack";
import classroomServices from "../../../services/classroomServices";
import Button from "../../Button";
import LoadingBtn from "../../LoadingBtn";
import Modal from "../../Modal";
import PreviewImportedStudentModal from "./PreviewImportedStudentModal";

const ImportClassroomsModal = forwardRef(({ onSubmit }, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { showSnack } = useSnack();
  const previewImportModalRef = useRef();
  const [teacherName, setTeacherName] = useState("");
  const [teacherId, setTeacherId] = useState(null);
  const [uploadedDataToPreview, setUploadedDataToPreview] = useState([]);
  const [fileToSend, setFileToSend] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const openPreviewImportModal = (data) => previewImportModalRef.current.openModal(data);

  const handlePreviewImport = () => {};

  useImperativeHandle(ref, () => ({
    openModal(receivedData) {
      setFileToSend(receivedData.file);
      setTeacherName(receivedData.teacherName);
      setTeacherId(receivedData.teacherId); // Expecting teacherId from receivedData
      setUploadedDataToPreview(receivedData.uploadedData);
      setIsOpen(true);
    },
    closeModal() {
      setErrorMessage("");
      setIsOpen(false);
    },
  }));

  const handleConfirm = async () => {
    setIsSubmitting(true);
    if (!teacherId || uploadedDataToPreview.length === 0) {
      console.error("Teacher ID or uploaded data is missing.");
      return;
    }

    const formData = new FormData();
    formData.append("file", fileToSend);

    try {
      const response = await classroomServices.importClassroomTemplate(teacherId, formData);
      showSnack(response.message, "success");
      setIsOpen(false);
      onSubmit();
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      setErrorMessage(error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setErrorMessage("");
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        header={
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2">
              <ImportStudentsIcon className="h-8 w-8 text-mutedText dark:text-dark-mutedText" />
              <span className="text-blue-500">{t("import_students")}</span>
            </p>
          </div>
        }
        footer={
          <div className="flex items-center justify-end space-x-2">
            <Button color="white" variant="outlined" onClick={() => setIsOpen(false)} label={t("cancel")} />
            <Button
              className="w-36"
              color="blue"
              disabled={isSubmitting}
              loading={isSubmitting}
              onClick={handleConfirm}
              label={t("import")}
            />
          </div>
        }
      >
        <p className="mb-6 text-start text-sm font-medium text-text dark:text-white">
          {t("import_the_following_classrooms_to")} {teacherName}?
        </p>

        <div className="max-h-80 overflow-y-auto">
          <table className="min-w-full border-collapse border border-gray-300 text-sm text-mutedText dark:border-gray-600 dark:text-dark-mutedText">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr className="text-left">
                <th className="border border-gray-300 px-4 py-2 dark:border-gray-600">
                  {t("classroom_name")}
                </th>
                <th className="border border-gray-300 px-4 py-2 dark:border-gray-600">{t("course")}</th>
                <th className="border border-gray-300 px-4 py-2 dark:border-gray-600">{t("section")}</th>
                <th className="border border-gray-300 px-4 py-2 dark:border-gray-600">{t("status")}</th>
                <th className="border border-gray-300 px-4 py-2 dark:border-gray-600">{t("students")}</th>
              </tr>
            </thead>
            <tbody>
              {uploadedDataToPreview?.map((row, rowIndex) => (
                <tr key={rowIndex} className="bg-white dark:bg-dark-background">
                  <td
                    title={row.name}
                    className="max-w-[10ch] truncate border border-gray-300 px-4 py-2 dark:border-gray-600"
                  >
                    {row.name}
                  </td>
                  <td
                    title={row.course_name}
                    className="max-w-[15ch] truncate border border-gray-300 px-4 py-2 dark:border-gray-600"
                  >
                    {row.course_name}
                  </td>
                  <td
                    title={row.section}
                    className="max-w-[15ch] truncate border border-gray-300 px-4 py-2 dark:border-gray-600"
                  >
                    {row.section}
                  </td>
                  <td
                    title={row.status}
                    className="max-w-[15ch] truncate border border-gray-300 px-4 py-2 dark:border-gray-600"
                  >
                    {row.status}
                  </td>
                  <td
                    onClick={() => openPreviewImportModal({ data: uploadedDataToPreview, index: rowIndex })}
                    className="cursor-pointer border border-gray-300 px-4 py-2 font-semibold text-blue-500 underline dark:border-gray-600"
                  >
                    {row.students.length}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {errorMessage && <p className="mt-2 font-medium text-red-400">* {errorMessage}</p>}
        </div>
      </Modal>
      <PreviewImportedStudentModal ref={previewImportModalRef} onSubmit={handlePreviewImport} />
    </>
  );
});

export default ImportClassroomsModal;
